import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { clientsApi, CLIENT_TYPE_LABEL, type Client, type ClientType } from "../api.js";
import { Modal } from "../components/Modal.js";
import { toast } from "../components/Toast.js";
import { btnStyle } from "../App.js";
import { useScreenView, trackSearchUsed } from "../lib/analytics.js";

type FormState = { name: string; client_type: ClientType; postal_code: string; address: string; tel: string; notes: string };
const EMPTY: FormState = { name: "", client_type: "individual", postal_code: "", address: "", tel: "", notes: "" };
const PHONE_NUMBER_ERROR = "電話番号はハイフンなしの半角数字で入力してください";
const PHONE_NUMBER_PATTERN = /^\d*$/;

export function ClientsPage() {
  useScreenView("clients");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [modal, setModal] = useState<"create" | { edit: Client } | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({ queryKey: ["clients"], queryFn: () => clientsApi.list() });

  const createMut = useMutation({
    mutationFn: () => clientsApi.create(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); setModal(null); toast("顧客を登録しました"); },
    onError: (e) => toast(e.message, "error"),
  });

  const updateMut = useMutation({
    mutationFn: (id: string) => clientsApi.update(id, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); setModal(null); toast("顧客情報を更新しました"); },
    onError: (e) => toast(e.message, "error"),
  });

  const deleteMut = useMutation({
    mutationFn: clientsApi.delete,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); toast("顧客を削除しました"); },
    onError: (e) => toast(e.message, "error"),
  });

  const filtered = (data?.data ?? []).filter((c) =>
    c.name.includes(search) || (c.address ?? "").includes(search) || (c.tel ?? "").includes(search),
  );

  // 検索利用の計測（入力が落ち着いてから1回。結果の有無のみ送る）
  const hasResult = filtered.length > 0;
  useEffect(() => {
    if (!search.trim()) return;
    const timer = setTimeout(() => trackSearchUsed("clients", hasResult), 800);
    return () => clearTimeout(timer);
  }, [search, hasResult]);

  function openCreate() { setForm(EMPTY); setModal("create"); }
  function openEdit(c: Client) { setForm({ name: c.name, client_type: c.client_type ?? "individual", postal_code: c.postal_code ?? "", address: c.address ?? "", tel: c.tel ?? "", notes: c.notes ?? "" }); setModal({ edit: c }); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (!PHONE_NUMBER_PATTERN.test(form.tel)) {
      toast(PHONE_NUMBER_ERROR, "error");
      return;
    }
    if (modal === "create") createMut.mutate();
    else if (modal && "edit" in modal) updateMut.mutate(modal.edit.id);
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>顧客管理</h1>
        <button style={btnStyle("primary")} onClick={openCreate}>+ 顧客を登録</button>
      </div>

      <input
        type="text" placeholder="名前・住所・電話で絞り込み"
        value={search} onChange={(e) => setSearch(e.target.value)}
        style={{ width: "100%", padding: "8px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 14, marginBottom: 16, boxSizing: "border-box" }}
      />

      {isLoading ? <p style={{ color: "#94a3b8" }}>読み込み中…</p> : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
              {["顧客名", "区分", "住所", "電話", ""].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#64748b", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "10px 12px" }}>
                  <button onClick={() => navigate(`/clients/${c.id}`)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#0f766e", fontWeight: 600, fontSize: 13, padding: 0 }}>
                    {c.name}
                  </button>
                </td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{
                    display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600,
                    background: c.client_type === "corporation" ? "#eff6ff" : "#f0fdf4",
                    color:      c.client_type === "corporation" ? "#1d4ed8" : "#16a34a",
                  }}>
                    {CLIENT_TYPE_LABEL[c.client_type ?? "individual"]}
                  </span>
                </td>
                <td style={{ padding: "10px 12px", color: "#475569" }}>{c.address ?? "—"}</td>
                <td style={{ padding: "10px 12px", color: "#475569" }}>{c.tel ?? "—"}</td>
                <td style={{ padding: "10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                  <button style={btnStyle("outline")} onClick={() => openEdit(c)}>編集</button>
                  {" "}
                  <button style={btnStyle("danger")} onClick={() => {
                    if (confirm(`「${c.name}」を削除しますか？`)) deleteMut.mutate(c.id);
                  }}>削除</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ padding: "24px 12px", textAlign: "center", color: "#94a3b8" }}>顧客が見つかりません</td></tr>
            )}
          </tbody>
        </table>
      )}

      {modal && (
        <Modal title={modal === "create" ? "顧客を登録" : "顧客を編集"} onClose={() => setModal(null)}>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>顧客区分</span>
              <select value={form.client_type}
                onChange={(e) => setForm((f) => ({ ...f, client_type: e.target.value as ClientType }))}
                style={{ padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 14 }}>
                <option value="individual">個人</option>
                <option value="corporation">法人</option>
              </select>
            </label>
            {([
              { key: "name",        label: "氏名・法人名 *", type: "text", placeholder: "例：山田太郎 / 株式会社サンプル" },
              { key: "postal_code", label: "郵便番号",       type: "text", placeholder: "例：444-0853" },
              { key: "address",     label: "住所",           type: "text", placeholder: "例：岡崎市三崎町9番地5" },
              { key: "tel",         label: "電話番号",       type: "text", placeholder: "例：0564528530" },
              { key: "notes",       label: "備考",           type: "text", placeholder: "連絡事項など" },
            ] as { key: keyof FormState; label: string; type: string; placeholder: string }[]).map(({ key, label, type, placeholder }) => (
              <label key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>{label}</span>
                <input
                  type={type}
                  value={form[key]}
                  placeholder={placeholder}
                  required={key === "name"}
                  inputMode={key === "tel" ? "numeric" : undefined}
                  pattern={key === "tel" ? "\\d*" : undefined}
                  title={key === "tel" ? PHONE_NUMBER_ERROR : undefined}
                  aria-invalid={key === "tel" && !PHONE_NUMBER_PATTERN.test(form.tel)}
                  aria-describedby={key === "tel" && !PHONE_NUMBER_PATTERN.test(form.tel) ? "client-tel-error" : undefined}
                  onInvalid={(e) => {
                    if (key === "tel") e.currentTarget.setCustomValidity(PHONE_NUMBER_ERROR);
                  }}
                  onInput={(e) => {
                    if (key === "tel") e.currentTarget.setCustomValidity("");
                  }}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  style={{
                    padding: "8px 10px",
                    border: key === "tel" && !PHONE_NUMBER_PATTERN.test(form.tel) ? "1px solid #dc2626" : "1px solid #cbd5e1",
                    borderRadius: 6,
                    fontSize: 14,
                  }}
                />
                {key === "tel" && !PHONE_NUMBER_PATTERN.test(form.tel) && (
                  <span id="client-tel-error" style={{ color: "#dc2626", fontSize: 12 }}>{PHONE_NUMBER_ERROR}</span>
                )}
              </label>
            ))}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
              <button type="button" style={btnStyle("outline")} onClick={() => setModal(null)}>キャンセル</button>
              <button type="submit" style={btnStyle("primary")} disabled={createMut.isPending || updateMut.isPending}>
                {modal === "create" ? "登録する" : "更新する"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
